import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete, UploadFile, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AmFilterBar, type AmFilters } from '../../components/AmFilterBar';
import { BulkImportDialog } from '../../components/BulkImportDialog';
import { amBulkApi } from '../../api/amBulk.api';
import { amStationsApi, type AmStation, type CreateStationInput } from '../../api/amStations.api';
import { clientsApi } from '../../api/clients.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';
import { StationFormDialog } from './StationFormDialog';
import { StationDetailDialog } from './StationDetailDialog';

export function StationsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState<AmFilters>({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AmStation | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<AmStation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['am-stations', page, limit, filters],
    queryFn: () => amStationsApi.list({ page, limit, search: filters.search || undefined, status: filters.status || undefined, clientId: filters.clientId || undefined, city: filters.city || undefined, propertyType: filters.propertyType || undefined, ownerType: filters.ownerType || undefined }),
  });
  const { data: clients } = useQuery({ queryKey: ['clients', 'all'], queryFn: () => clientsApi.list({ limit: 500 }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['am-stations'] });

  const saveMut = useMutation({
    mutationFn: (payload: CreateStationInput) => (editing ? amStationsApi.update(editing.id, payload) : amStationsApi.create(payload)),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => amStationsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const openNew = () => { setEditing(null); setError(null); setFormOpen(true); };
  const openEdit = (s: AmStation) => { setEditing(s); setError(null); setFormOpen(true); };

  const columns: Column<AmStation>[] = [
    { key: 'stationCode', label: 'Code', width: 120 },
    { key: 'stationName', label: 'Site Name' },
    { key: 'city', label: 'City' },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName ?? '—' },
    { key: 'ownerName', label: 'Owner', render: (r) => (r.ownerName ? <span>{r.ownerName}{r.ownerType === 'multiple' ? ' +' : ''}<br /><small style={{ color: '#888' }}>{r.ownerPhone ?? ''}</small></span> : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader title="Site Registration" subtitle="EV charging sites managed for clients — property, owners (rent split) & charger inventory."
        action={hasPermission('am_station.create') && (
          <Stack direction="row" spacing={1}>
            <Button startIcon={<UploadFile />} variant="outlined" onClick={() => setBulkOpen(true)}>Bulk Upload</Button>
            <Button startIcon={<Add />} variant="contained" onClick={openNew}>New Site</Button>
          </Stack>
        )} />

      <AmFilterBar value={filters} onChange={(f) => { setFilters(f); setPage(1); }} show={['search', 'status', 'client', 'city', 'property', 'owner']} statusOptions={['active', 'inactive']} searchPlaceholder="Search code / name / city / owner" />

      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="View"><IconButton size="small" onClick={() => setViewingId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('am_station.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('am_station.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <StationFormDialog
        open={formOpen} editing={editing} clients={clients?.data ?? []}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSave={(payload) => { setError(null); saveMut.mutate(payload); }}
        saving={saveMut.isPending} error={error}
      />
      <StationDetailDialog stationId={viewingId} onClose={() => setViewingId(null)} />

      <BulkImportDialog
        open={bulkOpen} onClose={() => setBulkOpen(false)} title="Bulk upload stations" entityLabel="Station" showCreateLogins={false}
        columns={[
          { header: 'Site Name', sample: 'ChargeFast Hub', required: true },
          { header: 'Client Name', sample: 'Acme Power Pvt Ltd' },
          { header: 'Address', sample: 'Sector 62' },
          { header: 'City', sample: 'Noida' },
          { header: 'State', sample: 'Uttar Pradesh' },
          { header: 'Owner Name', sample: 'Sunita Sharma' },
          { header: 'Owner Phone', sample: '9876543210' },
          { header: 'Owner Bank Account', sample: '50100123456' },
          { header: 'Owner IFSC', sample: 'HDFC0000123' },
          { header: 'Owner UPI', sample: 'sunita@upi' },
        ]}
        onImport={(rows) => amBulkApi.stations(rows)} onDone={invalidate}
      />
      <ConfirmDialog open={Boolean(deleting)} title="Delete station" message={`Soft-delete ${deleting?.stationName}?`} confirmLabel="Delete"
        onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
